import React, { useEffect, useRef } from "react";
import { UserPlus, Search, CreditCard, Star, RefreshCw } from "lucide-react";
import { Card } from "../../../../design-system/index.js";
import { cn } from "../../../../design-system/index.js";

const steps = [
  {
    icon: UserPlus,
    titulo: "Cadastro Completo",
    desc: "Preencha seu cadastro e receba respostas para quaisquer perguntas. Processo rápido e seguro.",
    color: "text-slate-blue-400",
    bg: "bg-slate-blue-400/12",
  },
  {
    icon: Search,
    titulo: "Seleção Responsável",
    desc: "Selecionamos candidatas com selo de qualidade para você escolher a babá ideal às suas necessidades.",
    color: "text-rose-400",
    bg: "bg-rose-400/12",
  },
  {
    icon: CreditCard,
    titulo: "Pagamento Seguro",
    desc: "Realize a compra do pacote de horas com segurança através da nossa plataforma.",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: Star,
    titulo: "Avaliação",
    desc: "Avalie a experiência com as babás e seus tesourinhos, ajudando outras famílias.",
    color: "text-amber-500",
    bg: "bg-amber-50",
  },
  {
    icon: RefreshCw,
    titulo: "Acompanhamento Contínuo",
    desc: "Suporte contínuo e renovação facilitada, sempre que você precisar.",
    color: "text-purple-500",
    bg: "bg-purple-50",
  },
];

export default function HowItWorksSection() {
  const itemsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.2 },
    );

    itemsRef.current.forEach((el) => el && observer.observe(el));
    return () => itemsRef.current.forEach((el) => el && observer.unobserve(el));
  }, []);

  return (
    <section id="como-funciona" className="bg-warm-50 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className={cn(
            "font-heading text-3xl sm:text-4xl font-bold",
            "text-warm-900"
          )}>
            Como Funciona
          </h2>
          <p className={cn(
            "mt-3 text-warm-500 max-w-md mx-auto",
            "text-sm sm:text-base"
          )}>
            Em poucos passos você encontra a babá ideal para o seu tesourinho.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Linha vertical (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-0.5 bg-gradient-to-b from-rose-400/30 via-warm-200 to-slate-blue-400/30 -translate-x-1/2" />

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-1 md:gap-0">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLeft = i % 2 === 0;
              return (
                <div
                  key={i}
                  ref={(el) => (itemsRef.current[i] = el)}
                  className={cn(
                    "opacity-0 translate-y-6 transition-all duration-500 ease-out",
                    "flex flex-col md:flex-row items-center gap-6 md:gap-10 py-6",
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  )}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  {/* Card */}
                  <Card className={cn(
                    "flex-1 rounded-2xl p-5",
                    isLeft ? "md:text-right" : "md:text-left"
                  )}>
                    <h4 className={cn(
                      "font-heading font-bold text-warm-900",
                      "text-base mb-1"
                    )}>
                      {step.titulo}
                    </h4>
                    <p className="text-sm text-warm-500 leading-relaxed">{step.desc}</p>
                  </Card>

                  {/* Number bubble */}
                  <div className="relative shrink-0 flex flex-col items-center z-10">
                    <div className={cn(
                      "flex h-14 w-14 items-center justify-center rounded-full",
                      step.bg,
                      "shadow-sm border border-warm-100"
                    )}>
                      <Icon size={22} className={step.color} />
                    </div>
                    <span className="mt-1 text-xs font-bold text-warm-400">{i + 1}</span>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden md:block" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}